import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import ROUTES from '../../routes';
import api from './../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

// Used by the backend when the general setting has no shipping charges
const FALLBACK_SHIPPING_CHARGES = 20;

const roundMoney = (value) => Math.round(value * 100) / 100;

function AddPaymentLink() {
    const formRef = useRef(null);
    const searchBoxRef = useRef(null);

    const [productSearch, setProductSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [product, setProduct] = useState(null);

    const [qty, setQty] = useState('1');
    const [price, setPrice] = useState('');
    const [shippingCharges, setShippingCharges] = useState('');
    const [defaultShipping, setDefaultShipping] = useState(FALLBACK_SHIPPING_CHARGES);
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [note, setNote] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [createdLink, setCreatedLink] = useState(null);

    // Default shipping shown as the placeholder and used in the preview
    useEffect(() => {
        api.get('/settings/general')
            .then((res) => {
                const value = res.data?.setting?.shipping_charges;
                if (value != null && value !== '') setDefaultShipping(Number(value));
            })
            .catch(() => { });
    }, []);

    // Debounced product search
    useEffect(() => {
        const term = productSearch.trim();
        if (!term || (product && term === product.title)) {
            setProductResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await api.get(`/product/list?limit=20&offset=0&search=${encodeURIComponent(term)}`);
                setProductResults(res.data.products || []);
                setShowResults(true);
            } catch (err) {
                handleErrors(err);
            } finally {
                setSearching(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [productSearch, product]);

    // Close the results list when clicking elsewhere
    useEffect(() => {
        const onClick = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useEffect(() => {
        const $ = window.jQuery;
        if (formRef.current && $?.fn?.validate) {
            $(formRef.current).validate({
                rules: {
                    product_id: { required: true },
                    qty: { required: true, digits: true, min: 1 },
                    price: { number: true, min: 0.01 },
                    shipping_charges: { number: true, min: 0 },
                    customer_email: { email: true },
                },
                messages: {
                    product_id: { required: "Please select a product" },
                    qty: {
                        required: "Please enter a quantity",
                        digits: "Quantity must be a whole number",
                        min: "Quantity must be at least 1"
                    },
                    price: { min: "Price must be greater than 0" },
                    customer_email: { email: "Please enter a valid email" },
                },
                ignore: [],
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                errorPlacement: function (error, element) {
                    if (element.attr('name') === 'product_id') {
                        error.insertAfter($(searchBoxRef.current));
                    } else {
                        error.insertAfter(element);
                    }
                },
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, []);

    const selectProduct = (p) => {
        setProduct(p);
        setProductSearch(p.title);
        setShowResults(false);
        setProductResults([]);
        // Re-validate the hidden field once it has a value
        setTimeout(() => window.jQuery?.(formRef.current)?.find('[name="product_id"]').valid?.(), 0);
    };

    const clearProduct = () => {
        setProduct(null);
        setProductSearch('');
    };

    const qtyNumber = parseInt(qty, 10) || 0;
    const unitPrice = price !== '' ? Number(price) : Number(product?.price) || 0;
    const shipping = shippingCharges !== '' ? Number(shippingCharges) : defaultShipping;
    const subTotal = roundMoney(unitPrice * qtyNumber);
    const total = roundMoney(subTotal + (shipping || 0));
    const stock = typeof product?.qty === 'number' ? product.qty : null;

    const resetForm = () => {
        clearProduct();
        setQty('1');
        setPrice('');
        setShippingCharges('');
        setCustomerName('');
        setCustomerEmail('');
        setCustomerPhone('');
        setNote('');
    };

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);
        if (!form.valid()) return;

        const payload = { product_id: product._id, qty: qtyNumber };
        if (price !== '') payload.price = Number(price);
        if (shippingCharges !== '') payload.shipping_charges = Number(shippingCharges);
        if (customerName.trim()) payload.customer_name = customerName.trim();
        if (customerEmail.trim()) payload.customer_email = customerEmail.trim();
        if (customerPhone.trim()) payload.customer_phone = customerPhone.trim();
        if (note.trim()) payload.note = note.trim();

        try {
            setSubmitting(true);
            const res = await api.post('/payment-link/create', payload);
            showToast('success', res.data.message, 'Success');
            setCreatedLink(res.data.payment_link);
            resetForm();
            form.find('.is-valid, .is-invalid').removeClass('is-valid is-invalid');
            form.find('label.is-invalid').remove();
        } catch (err) {
            handleErrors(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(createdLink.url);
            showToast('success', 'Payment link copied to clipboard', 'Copied');
        } catch (err) {
            showToast('error', 'Could not copy the link. Please copy it manually.', 'Error');
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.PAYMENT_LINKS}>Payment Links</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.ADD_PAYMENT_LINK}>Create Payment Link</Link>
                    </li>
                </ol>
            </div>

            {createdLink && (
                <div className="row">
                    <div className="col-12">
                        <div className="card h-auto">
                            <div className="card-header">
                                <h4 className="card-title">Payment link created</h4>
                            </div>
                            <div className="card-body">
                                <p className="mb-2">
                                    {createdLink.qty} × {createdLink.title} — total <strong>{Number(createdLink.total_amount).toFixed(2)}</strong>.
                                    Send this link to the customer:
                                </p>
                                <div className="input-group mb-3">
                                    <input type="text" className="form-control" value={createdLink.url} readOnly onFocus={(e) => e.target.select()} />
                                    <button type="button" className="btn btn-primary" onClick={handleCopy}>
                                        <i className="fas fa-copy me-1"></i>Copy
                                    </button>
                                </div>
                                <Link to={ROUTES.PAYMENT_LINKS} className="btn btn-sm btn-secondary light">
                                    <i className="fas fa-list me-1"></i>Back to payment links
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-12">
                    <div className="card h-auto">
                        <div className="card-header">
                            <h4 className="card-title">{createdLink ? 'Create another payment link' : 'Create Payment Link'}</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate onSubmit={(e) => e.preventDefault()}>
                                <div className="row">
                                    <div className="mb-3 col-lg-8">
                                        <label className="form-label">Product<sup style={{ color: 'red' }}>*</sup></label>
                                        <div ref={searchBoxRef} className="position-relative">
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by title or OE part number..."
                                                    value={productSearch}
                                                    onChange={(e) => {
                                                        setProductSearch(e.target.value);
                                                        if (product) setProduct(null);
                                                    }}
                                                    onFocus={() => productResults.length && setShowResults(true)}
                                                    autoComplete="off"
                                                />
                                                {product && (
                                                    <button type="button" className="btn btn-outline-secondary" onClick={clearProduct} title="Clear">
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                            {showResults && (searching || productResults.length > 0 || productSearch.trim()) && !product && (
                                                <div
                                                    className="list-group position-absolute w-100 shadow"
                                                    style={{ zIndex: 1000, maxHeight: 300, overflowY: 'auto' }}
                                                >
                                                    {searching && <div className="list-group-item">Searching...</div>}
                                                    {!searching && productResults.length === 0 && (
                                                        <div className="list-group-item">No products found</div>
                                                    )}
                                                    {!searching && productResults.map((p) => {
                                                        const outOfStock = typeof p.qty === 'number' && p.qty <= 0;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={p._id}
                                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                                onClick={() => selectProduct(p)}
                                                            >
                                                                <span>
                                                                    {p.title}
                                                                    {p.oe_part_number && <small className="text-muted ms-2">{p.oe_part_number}</small>}
                                                                </span>
                                                                <span className={`badge badge-${outOfStock ? 'danger' : 'light'} ms-2`}>
                                                                    {typeof p.qty === 'number' ? (outOfStock ? 'Out of stock' : `Stock: ${p.qty}`) : 'Stock: -'}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <input type="hidden" name="product_id" value={product?._id || ''} />
                                        {product && (
                                            <small className={`d-block mt-1 ${stock !== null && stock < qtyNumber ? 'text-danger' : 'text-muted'}`}>
                                                Website price: {product.price !== '' && product.price != null ? Number(product.price).toFixed(2) : 'not set'}
                                                {' · '}In stock: {stock !== null ? stock : 'not tracked'}
                                            </small>
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-4">
                                        <label className="form-label">Quantity<sup style={{ color: 'red' }}>*</sup></label>
                                        <input
                                            name="qty"
                                            type="number"
                                            min="1"
                                            step="1"
                                            className="form-control"
                                            value={qty}
                                            onChange={(e) => setQty(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Unit Price</label>
                                        <input
                                            name="price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="form-control"
                                            placeholder={product?.price ? `Product price: ${product.price}` : 'Product price'}
                                            value={price}
                                            onChange={(e) => setPrice(e.target.value)}
                                        />
                                        <small className="text-muted">Leave blank to use the product's price.</small>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Shipping Charges</label>
                                        <input
                                            name="shipping_charges"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            className="form-control"
                                            placeholder={`Default: ${defaultShipping}`}
                                            value={shippingCharges}
                                            onChange={(e) => setShippingCharges(e.target.value)}
                                        />
                                        <small className="text-muted">Leave blank to use the default from settings.</small>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Total</label>
                                        <div className="form-control bg-light">
                                            {product ? (
                                                <>
                                                    {subTotal.toFixed(2)} + {Number(shipping || 0).toFixed(2)} shipping = <strong>{total.toFixed(2)}</strong>
                                                </>
                                            ) : '-'}
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Customer Name</label>
                                        <input
                                            name="customer_name"
                                            type="text"
                                            className="form-control"
                                            value={customerName}
                                            onChange={(e) => setCustomerName(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Customer Email</label>
                                        <input
                                            name="customer_email"
                                            type="email"
                                            className="form-control"
                                            value={customerEmail}
                                            onChange={(e) => setCustomerEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-4 col-md-6">
                                        <label className="form-label">Customer Phone</label>
                                        <input
                                            name="customer_phone"
                                            type="text"
                                            className="form-control"
                                            value={customerPhone}
                                            onChange={(e) => setCustomerPhone(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Note</label>
                                        <textarea
                                            name="note"
                                            className="form-control"
                                            rows={3}
                                            placeholder="Optional. The customer can see this on the payment page."
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <i className="spinner-border spinner-border-sm me-1"></i>
                                        ) : (
                                            <i className="fas fa-link me-1"></i>
                                        )}
                                        Create Link
                                    </button>
                                    <Link to={ROUTES.PAYMENT_LINKS} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddPaymentLink;
